import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Heading as BaseHeading, useBrand } from '@cof/plastic-components';
import { css, styled } from 'styled-components';

import Header from '../../components/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import Main from '../../components/Main';
import TitleSection from '../../components/TitleSection';
import { withText } from '../../text/textStore';
import useBackground from '../../utilities/useBackground';
import useIcon from '../../utilities/useIcon';
import OpenBankingPostSubmitModal from '../../components/experiments/variants/OpenBankingAcceptRegionTest/ObOptInJourneyPostSubmitModal';
import { findMbox } from '../../components/experiments/offers';
import { useAdobeExperiments } from '../../experiments/AdobeExperiment/AdobeExperimentProvider';
import { OB_ACCEPT_MODAL_VARIANT } from '../../components/experiments/variants/OpenBankingAcceptRegionTest/variants';
import { addNewRelicPageAction } from '../../utilities/newRelic';
import { CONSUMER_NAMES } from '../../utilities/constants';

export const Heading = styled(BaseHeading).attrs(() => ({ level: '1', size: 'small', mt: 'md' }))`
    ${({ theme }: any) => css`
        color: ${theme.colors.global.white};
        ${theme.components.SpinnerHeading}
    `}
`;

Heading.displayName = 'Heading';

const TitleSectionStyled = styled(TitleSection)`
    height: 100%;
`;
TitleSectionStyled.displayName = 'TitleSectionStyled';

const Spinner = ({ Text, agreementUrl, appendObSkippedQueryParam = false }: any) => {
    const adobeState = useAdobeExperiments();
    const background = useBackground('spinner');
    const icon = useIcon('spinner', true);
    const brand = useBrand();
    const consumerName = CONSUMER_NAMES[brand];

    useEffect(() => {
        const getRedirectUrl = () => {
            if (!appendObSkippedQueryParam) return agreementUrl;
            const newAgreementUrl = new URL(agreementUrl);
            newAgreementUrl.searchParams.append('obSkipped', 'true');
            return newAgreementUrl.href;
        };

        const handlePageShow = (event: any) => {
            if (event.persisted && agreementUrl) {
                addNewRelicPageAction('Spinner - Redirected to Agreements', {
                    reason: 'user_returned_to_page',
                    consumerName: consumerName,
                    hasConnectionUrl: true
                });
                window.location.assign(getRedirectUrl());
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [agreementUrl, consumerName, appendObSkippedQueryParam]);

    const acceptRegionVariant = findMbox(
        // @ts-expect-error TS2339: Property 'data' does not exist on type '{}'.
        adobeState.data,
        'quotations-ob-accept-region-opt-in-mbox'
    )?.content?.variant;
    const showOpenBankingModal = acceptRegionVariant === OB_ACCEPT_MODAL_VARIANT && agreementUrl;

    return (
        <>
            <Header />
            <Main bg={background} data-qa-id="spinner">
                {showOpenBankingModal && <OpenBankingPostSubmitModal agreementUrl={agreementUrl} />}
                <TitleSectionStyled>
                    <LoadingSpinner Icon={icon} features={{ resultSpinner: true }}>
                        <Heading data-qa-id="page-title">
                            <Text path="Title" />
                        </Heading>
                    </LoadingSpinner>
                </TitleSectionStyled>
            </Main>
        </>
    );
};

Spinner.propTypes = {
    Text: PropTypes.func.isRequired,
    redirectUrl: PropTypes.string,
    agreementUrl: PropTypes.string,
    appendObSkippedQueryParam: PropTypes.bool
};

const WrappedSpinner = withText('Spinner')(Spinner);

WrappedSpinner.displayName = 'Spinner';

const ObRedirectSpinnerBase = (props: any) => <Spinner {...props} appendObSkippedQueryParam />;

export const ObRedirectSpinner = withText('ObRedirectSpinner')(ObRedirectSpinnerBase);

ObRedirectSpinner.displayName = 'ObRedirectSpinner';

export default WrappedSpinner;
